/**
 * Le impostazioni dell'applicazione, caricate una volta all'avvio.
 *
 * Perche' un magazzino a parte e non `useStore`: quello tiene lo stato
 * dell'interfaccia (menu aperto, notifiche) e lo salva nel browser. Queste
 * invece stanno nel database e valgono per tutta l'azienda: se due persone
 * usano due copie diverse dei dati fiscali, i documenti divergono.
 *
 * Il caricamento aggiorna anche `BRANDING` in lib/branding.ts, che e' letto
 * da chi non e' un componente React (generazione PDF, template email).
 */

import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import {
    applyCompanyProfile,
    normalizzaCompanyProfile,
    BRANDING,
    BRANDING_DA_ENV,
    type Branding
} from '../lib/branding';
import {
    LEADS_SETTINGS_DEFAULT,
    SETTINGS_KEYS,
    leggiLeadsSettings,
    scriviLeadsSettings,
    type LeadsSettings
} from '../types/settings.types';

interface SettingsStore {
    /** false finche' la prima lettura dal database non e' finita. */
    caricato: boolean;
    caricamentoInCorso: boolean;
    azienda: Branding;
    lead: LeadsSettings;
    fattureAttive: boolean;

    carica: () => Promise<void>;
    salvaAzienda: (dati: Branding) => Promise<void>;
    salvaLead: (dati: LeadsSettings) => Promise<void>;
    salvaFattureAttive: (attive: boolean) => Promise<void>;
}

/** Scrive una riga di `app_settings`. Solleva l'errore: lo gestisce la pagina. */
async function scriviRiga(key: string, value: unknown): Promise<void> {
    const { error } = await supabase
        .from('app_settings')
        .upsert({ key, value } as never, { onConflict: 'key' });
    if (error) throw error;
}

export const useSettingsStore = create<SettingsStore>((set, get) => ({
    caricato: false,
    caricamentoInCorso: false,
    azienda: { ...BRANDING_DA_ENV },
    lead: LEADS_SETTINGS_DEFAULT,
    fattureAttive: true,

    carica: async () => {
        if (get().caricamentoInCorso) return;
        set({ caricamentoInCorso: true });

        const { data, error } = await supabase
            .from('app_settings')
            .select('key, value')
            .in('key', [SETTINGS_KEYS.azienda, SETTINGS_KEYS.lead, SETTINGS_KEYS.fatture]);

        if (error) {
            // Senza impostazioni l'app deve comunque partire: restano i valori
            // delle variabili d'ambiente, che sono quelli di prima.
            console.error('Impostazioni non caricate, si usano i valori di partenza:', error);
            set({ caricamentoInCorso: false, caricato: true });
            return;
        }

        const riga = (key: string) => data?.find((r) => r.key === key)?.value;

        applyCompanyProfile(riga(SETTINGS_KEYS.azienda));
        const fatture = riga(SETTINGS_KEYS.fatture) as { enabled?: boolean } | undefined;

        set({
            azienda: { ...BRANDING },
            lead: leggiLeadsSettings(riga(SETTINGS_KEYS.lead)),
            fattureAttive: fatture?.enabled === undefined ? true : Boolean(fatture.enabled),
            caricato: true,
            caricamentoInCorso: false
        });
    },

    salvaAzienda: async (dati) => {
        const pulito = normalizzaCompanyProfile(dati);
        await scriviRiga(SETTINGS_KEYS.azienda, pulito);
        applyCompanyProfile(pulito);
        set({ azienda: { ...BRANDING } });
    },

    salvaLead: async (dati) => {
        await scriviRiga(SETTINGS_KEYS.lead, scriviLeadsSettings(dati));
        set({ lead: dati });
    },

    salvaFattureAttive: async (attive) => {
        await scriviRiga(SETTINGS_KEYS.fatture, { enabled: attive });
        set({ fattureAttive: attive });
    }
}));

/** Scorciatoia per i componenti che vogliono solo le impostazioni dei lead. */
export const useLeadSettings = (): LeadsSettings => useSettingsStore((s) => s.lead);
